import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { PawPrint, Lock, ArrowRight, CheckCircle2, XCircle } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";

export default function Activate() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [state, setState] = useState("checking"); // checking | valid | invalid
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      if (!token) { setState("invalid"); return; }
      try {
        const { data } = await api.post("/auth/activate/validate", { token });
        if (active) { setEmail(data.email); setState("valid"); }
      } catch {
        if (active) setState("invalid");
      }
    })();
    return () => { active = false; };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (pw !== confirm) { toast.error("Passwords do not match"); return; }
    setBusy(true);
    try {
      await api.post("/auth/activate", { token, new_password: pw });
      toast.success("Your account is activated. Please sign in.");
      navigate("/login");
    } catch (err) {
      toast.error(apiErr(err));
      // A consumed/expired token can no longer be used.
      if (err?.response?.status === 400) setState("invalid");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      <div className="relative hidden lg:flex flex-col justify-between bg-slate-900 p-12 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.18]" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #9E86FD 0, transparent 40%), radial-gradient(circle at 80% 60%, #6A48F0 0, transparent 40%)" }} />
        <div className="relative flex items-center gap-2.5">
          <div className="h-10 w-10 rounded-xl pawkt-gradient-primary flex items-center justify-center shadow-purpleGlow"><PawPrint className="h-6 w-6 text-white" /></div>
          <span className="font-display text-2xl font-extrabold tracking-tight text-white">PAWKT</span>
        </div>
        <div className="relative">
          <h1 className="font-display text-4xl font-extrabold leading-tight text-white">Activate your account</h1>
          <p className="mt-4 text-slate-300 max-w-md leading-relaxed">Set your password to join your team on Pawkt. Your role and access are already configured by your business owner.</p>
        </div>
        <div className="relative text-xs text-slate-500">© 2026 PAWKT · Pet Business Operating System</div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm" data-testid="activate-page">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div className="h-9 w-9 rounded-xl pawkt-gradient-primary flex items-center justify-center shadow-purpleGlow"><PawPrint className="h-5 w-5 text-white" /></div>
            <span className="font-display text-xl font-extrabold tracking-tight">PAWKT</span>
          </div>

          {state === "checking" && <p className="text-sm text-slate-500" data-testid="activate-checking">Checking your activation link…</p>}

          {state === "invalid" && (
            <div data-testid="activate-invalid">
              <div className="flex items-center gap-2 text-rose-600"><XCircle className="h-6 w-6" /><h2 className="font-display text-xl font-bold">Link not valid</h2></div>
              <p className="mt-2 text-sm text-slate-500">This activation link is invalid, already used, or has expired. Please ask your business owner to generate a new activation link.</p>
              <button onClick={() => navigate("/login")} data-testid="activate-go-login" className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-bold text-white shadow-purpleGlow">Go to sign in <ArrowRight className="h-4 w-4" /></button>
            </div>
          )}

          {state === "valid" && (
            <div>
              <div className="flex items-center gap-2 text-emerald-600"><CheckCircle2 className="h-6 w-6" /><h2 className="font-display text-xl font-bold text-slate-900">Set your password</h2></div>
              <p className="text-sm text-slate-500 mt-1">Activating <span className="font-semibold text-slate-700" data-testid="activate-email">{email}</span></p>
              <form onSubmit={submit} className="space-y-3 mt-6">
                <Field icon={Lock} type="password" placeholder="New password (min 8 chars)" value={pw} onChange={(e) => setPw(e.target.value)} testid="activate-password" required />
                <Field icon={Lock} type="password" placeholder="Confirm password" value={confirm} onChange={(e) => setConfirm(e.target.value)} testid="activate-confirm" required />
                <button type="submit" disabled={busy} data-testid="activate-submit" className="flex w-full items-center justify-center gap-2 rounded-full bg-primary py-3 text-sm font-bold text-white shadow-purpleGlow hover:bg-pawkt-primary-hover disabled:opacity-60">
                  {busy ? "Activating…" : "Activate & continue"} <ArrowRight className="h-4 w-4" />
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ icon: Icon, testid, ...props }) {
  return (
    <div className="relative">
      <Icon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
      <input {...props} data-testid={testid} className="w-full rounded-[14px] border border-pawkt-border bg-white pl-10 pr-3 py-3 text-sm outline-none transition-colors focus:border-primary focus:ring-2 focus:ring-primary/15" />
    </div>
  );
}
